/* -------------------------------------------------- */
/*  src/pages/NewPartner.jsx                          */
/* -------------------------------------------------- */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import "./PartnerApply.css";

export default function NewPartner() {
  /* ───────────── 基础信息 + 联系人 + SEO ───────────── */
  const [base, setBase] = useState({
    name: "",
    logo: "",
    shortDesc: "",
    hashtags: "",
    /* SEO */
    seoTitle: "",
    seoDesc: "",
    status: "approved",
    contactName: "",
    contactEmail: "",
    contactLinkedin: "",
    contactPhone: "",
    contactNote: "",
  });

  /* ───────────── 详情页 ───────────── */
  const [details, setDetails] = useState({
    longDesc: "",
    docLinks: [""],
    files: [],
  });

  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  /* ───────────── Handlers ───────────── */
  const handleBaseChange = (e) =>
    setBase({ ...base, [e.target.name]: e.target.value });

  const handleDetailChange = (e) =>
    setDetails({ ...details, [e.target.name]: e.target.value });

  const handleDocLinkChange = (i, v) => {
    const next = [...details.docLinks];
    next[i] = v;
    setDetails({ ...details, docLinks: next });
  };

  const addDocLink = () =>
    setDetails({ ...details, docLinks: [...details.docLinks, ""] });

  const removeDocLink = (i) =>
    setDetails({
      ...details,
      docLinks: details.docLinks.filter((_, idx) => idx !== i),
    });

  const handleFileChange = (e) =>
    setDetails({ ...details, files: [...details.files, ...Array.from(e.target.files)] });

  const removeFile = (i) =>
    setDetails({
      ...details,
      files: details.files.filter((_, idx) => idx !== i),
    });

  /* ───────────── 上传文件到 Firebase Storage ───────────── */
  const uploadFiles = async (slug) => {
    const uploads = details.files.map(async (file) => {
      const fileRef = ref(storage, `partners/${slug}/${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      return { name: file.name, url };
    });
    return Promise.all(uploads); // [{name,url}, …]
  };

  /* ───────────── 提交 ───────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const slug = base.name.trim().toLowerCase().replace(/\s+/g, "-");

    let uploadedFiles = [];
    try {
      uploadedFiles = await uploadFiles(slug);
    } catch (err) {
      console.error(err);
      alert("File upload failed, check console.");
      setSending(false);
      return;
    }

    const docData = {
      /* —— 卡片信息 —— */
      name: base.name,
      description: base.shortDesc,
      hashtags: base.hashtags.split(",").map((s) => s.trim()).filter(Boolean),
      logo: base.logo,
      status: base.status,
      views: 0,

      /* —— 联系人 —— */
      contact: {
        name: base.contactName,
        email: base.contactEmail,
        linkedin: base.contactLinkedin,
        phone: base.contactPhone,
        note: base.contactNote,
      },

      /* —— SEO 元数据 —— */
      seo: {
        title: base.seoTitle || base.name,
        desc:  base.seoDesc  || base.shortDesc,
        image: base.logo,
        keywords: base.hashtags,          // 直接存字符串方便改动
      },

      /* —— 详情页 —— */
      longDesc: details.longDesc,
      docLinks: details.docLinks.filter(Boolean),
      files: uploadedFiles,
      createdAt: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, "partners", slug), docData);
      alert("✅ Partner saved!");
      navigate(`/partners/${slug}`);
    } catch (err) {
      console.error(err);
      alert("Write failed, check console.");
      setSending(false);
    }
  };

  /* ───────────── UI ───────────── */
  return (
    <div className="apply-container">
      <Link to="/partners" className="apply-back-btn">← Back</Link>
      <h1>Create / Edit Partner</h1>

      <form className="apply-form" onSubmit={handleSubmit}>
        {/* ── Card Info ── */}
        <h3>Card Info</h3>
        <label>
          Business Name
          <input name="name" value={base.name} onChange={handleBaseChange} required />
        </label>

        <label>
          Logo URL
          <input name="logo" value={base.logo} onChange={handleBaseChange} />
        </label>

        <label>
          Short Description
          <textarea
            name="shortDesc"
            rows={2}
            value={base.shortDesc}
            onChange={handleBaseChange}
          />
        </label>

        <label>
          Hashtags <small>(comma-separated)</small>
          <input
            name="hashtags"
            placeholder="web3, defi, ai, …"
            value={base.hashtags}
            onChange={handleBaseChange}
          />
        </label>

        {/* ── SEO 额外字段 ── */}
        <h4 style={{ marginTop: 16 }}>SEO Meta</h4>
        <label>
          SEO Title
          <input
            name="seoTitle"
            placeholder="Leave blank to use the Business Name by default"
            value={base.seoTitle}
            onChange={handleBaseChange}
          />
        </label>

        <label>
          SEO Description <small>(≤155 chars)</small>
          <textarea
            name="seoDesc"
            rows={3}
            value={base.seoDesc}
            onChange={handleBaseChange}
          />
        </label>

        <label>
          Status
          <select name="status" value={base.status} onChange={handleBaseChange}>
            <option value="approved">approved</option>
            <option value="proposed">proposed</option>
          </select>
        </label>

        {/* ── Point of Contact ── */}
        <h3 style={{ marginTop: 32 }}>Point of Contact</h3>
        <label>
          Name
          <input name="contactName" value={base.contactName} onChange={handleBaseChange} />
        </label>

        <label>
          Email
          <input
            name="contactEmail"
            type="email"
            value={base.contactEmail}
            onChange={handleBaseChange}
          />
        </label>

        <label>
          Linkedin
          <input
            name="contactLinkedin"
            placeholder="https://linkedin.com/in/…"
            value={base.contactLinkedin}
            onChange={handleBaseChange}
          />
        </label>

        <label>
          Phone
          <input name="contactPhone" value={base.contactPhone} onChange={handleBaseChange} />
        </label>

        <label>
          Personal Note
          <textarea
            name="contactNote"
            rows={2}
            value={base.contactNote}
            onChange={handleBaseChange}
          />
        </label>

        {/* ── Detail Page ── */}
        <h3 style={{ marginTop: 32 }}>Detail Page</h3>
        <label>
          Long Description
          <textarea
            name="longDesc"
            rows={4}
            value={details.longDesc}
            onChange={handleDetailChange}
          />
        </label>

        {/* Doc Links 动态数组 */}
        <label style={{ display: "block", marginBottom: 8 }}>
          Doc Link(s)
          {details.docLinks.map((url, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <input
                value={url}
                placeholder="https://…"
                style={{ flex: 1 }}
                onChange={(e) => handleDocLinkChange(i, e.target.value)}
              />
              <button type="button" onClick={() => removeDocLink(i)}>✕</button>
            </div>
          ))}
          <button type="button" onClick={addDocLink} style={{ marginTop: 6, fontSize: 14 }}>
            ＋ Add Link
          </button>
        </label>

        {/* 文件上传 */}
        <label>
          Upload Files (doc / video / audio / any)
          <input type="file" multiple onChange={handleFileChange} />
          {details.files.length > 0 && (
            <ul className="file-list">
              {details.files.map((f, i) => (
                <li key={f.name} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {f.name}
                  <button type="button" onClick={() => removeFile(i)}>✕</button>
                </li>
              ))}
            </ul>
          )}
        </label>

        <button type="submit" className="apply-submit" disabled={sending}>
          {sending ? "Saving…" : "Save Partner"}
        </button>
      </form>
    </div>
  );
}
